import React, { useState } from 'react';
import { Building2, Globe, Clock, Users, Plus, Trash2, Check, ArrowRight, ArrowLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [departments, setDepartments] = useState(['Engineering', 'Sales', 'Marketing']);
  const [newDept, setNewDept] = useState('');
  const [invites, setInvites] = useState([{
    email: 'sarah@acme.com',
    role: 'Manager'
  }, {
    email: 'mike@acme.com',
    role: 'Traveler'
  }]);
  const [newInvite, setNewInvite] = useState({
    email: '',
    role: 'Traveler'
  });
  const handleAddDept = () => {
    if (newDept && !departments.includes(newDept)) {
      setDepartments([...departments, newDept]);
      setNewDept('');
    }
  };
  const handleAddInvite = () => {
    if (newInvite.email) {
      setInvites([...invites, newInvite]);
      setNewInvite({
        email: '',
        role: 'Traveler'
      });
    }
  };
  const handleComplete = () => {
    // In a real app, this would save all onboarding data
    navigate('/');
  };
  return <div className="max-w-4xl mx-auto py-8" data-id="element-1006">
      <div className="mb-8 text-center" data-id="element-1007">
        <h1 className="text-3xl font-bold text-slate-900" data-id="element-1008">
          Welcome to TravelHub!
        </h1>
        <p className="mt-2 text-slate-500" data-id="element-1009">
          Let's get your organization set up in a few simple steps.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-10" data-id="element-1010">
        <div className="flex items-center justify-between relative max-w-2xl mx-auto" data-id="element-1011">
          <div className="absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 bg-slate-200 -z-10" data-id="element-1012"></div>

          {[{
          num: 1,
          label: 'Organization'
        }, {
          num: 2,
          label: 'Departments'
        }, {
          num: 3,
          label: 'Policies'
        }, {
          num: 4,
          label: 'Team'
        }].map(s => <div key={s.num} className="flex flex-col items-center bg-slate-50 px-2" data-id="element-1013">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${step > s.num ? 'bg-emerald-500 border-emerald-500 text-white' : step === s.num ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300 text-slate-400'}`} data-id="element-1014">
                {step > s.num ? <Check className="h-6 w-6" data-id="element-1015" /> : s.num}
              </div>
              <span className={`mt-2 text-xs font-medium ${step === s.num ? 'text-blue-600' : 'text-slate-500'}`} data-id="element-1016">
                {s.label}
              </span>
            </div>)}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-1017">
        <div className="p-8" data-id="element-1018">
          {step === 1 && <div className="space-y-6 animate-in fade-in slide-in-from-right-4" data-id="element-1019">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2" data-id="element-1020">
                <Building2 className="h-5 w-5 text-blue-600" data-id="element-1021" /> Organization
                Setup
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1022">
                <div data-id="element-1023">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1024">
                    Company Logo
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-slate-300 border-dashed rounded-lg hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-1025">
                    <div className="space-y-1 text-center" data-id="element-1026">
                      <div className="mx-auto h-12 w-12 text-slate-400" data-id="element-1027">
                        <svg className="mx-auto h-12 w-12" stroke="currentColor" fill="none" viewBox="0 0 48 48" data-id="element-1028">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1029" />
                        </svg>
                      </div>
                      <div className="flex text-sm text-slate-600 justify-center" data-id="element-1030">
                        <span className="relative rounded-md bg-white font-medium text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 hover:text-blue-500" data-id="element-1031">
                          Upload a file
                        </span>
                      </div>
                      <p className="text-xs text-slate-500" data-id="element-1032">
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-1033">
                  <div data-id="element-1034">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1035">
                      Timezone
                    </label>
                    <div className="relative" data-id="element-1036">
                      <Clock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" data-id="element-1037" />
                      <select className="block w-full rounded-lg border border-slate-300 pl-10 p-2.5 focus:border-blue-500 focus:ring-blue-500" data-id="element-1038">
                        <option data-id="element-1039">(GMT-05:00) Eastern Time</option>
                        <option data-id="element-1040">(GMT-08:00) Pacific Time</option>
                        <option data-id="element-1041">(GMT+00:00) London</option>
                        <option data-id="element-1042">(GMT+01:00) Paris</option>
                      </select>
                    </div>
                  </div>

                  <div data-id="element-1043">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1044">
                      Default Currency
                    </label>
                    <div className="relative" data-id="element-1045">
                      <Globe className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" data-id="element-1046" />
                      <select className="block w-full rounded-lg border border-slate-300 pl-10 p-2.5 focus:border-blue-500 focus:ring-blue-500" data-id="element-1047">
                        <option data-id="element-1048">USD ($)</option>
                        <option data-id="element-1049">EUR (€)</option>
                        <option data-id="element-1050">GBP (£)</option>
                        <option data-id="element-1051">JPY (¥)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {step === 2 && <div className="space-y-6 animate-in fade-in slide-in-from-right-4" data-id="element-1052">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2" data-id="element-1053">
                <Users className="h-5 w-5 text-blue-600" data-id="element-1054" /> Departments
              </h2>
              <p className="text-sm text-slate-500" data-id="element-1055">
                Add departments to organize your team and budgets.
              </p>

              <div className="flex gap-2" data-id="element-1056">
                <input type="text" value={newDept} onChange={e => setNewDept(e.target.value)} placeholder="e.g. Finance" className="flex-1 rounded-lg border border-slate-300 p-2.5 focus:border-blue-500 focus:ring-blue-500" onKeyDown={e => e.key === 'Enter' && handleAddDept()} data-id="element-1057" />
                <button onClick={handleAddDept} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center" data-id="element-1058">
                  <Plus className="h-4 w-4 mr-1" data-id="element-1059" /> Add
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-1060">
                {departments.map(dept => <div key={dept} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200" data-id="element-1061">
                    <span className="font-medium text-slate-700" data-id="element-1062">{dept}</span>
                    <button onClick={() => setDepartments(departments.filter(d => d !== dept))} className="text-slate-400 hover:text-red-500" data-id="element-1063">
                      <Trash2 className="h-4 w-4" data-id="element-1064" />
                    </button>
                  </div>)}
              </div>
            </div>}

          {step === 3 && <div className="space-y-6 animate-in fade-in slide-in-from-right-4" data-id="element-1065">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2" data-id="element-1066">
                <Shield className="h-5 w-5 text-blue-600" data-id="element-1067" /> Travel Policies
              </h2>
              <p className="text-sm text-slate-500" data-id="element-1068">
                Set default limits for your travelers.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1069">
                <div data-id="element-1070">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1071">
                    Domestic Flight Cap
                  </label>
                  <div className="flex items-center gap-2" data-id="element-1072">
                    <span className="text-slate-500" data-id="element-1073">$</span>
                    <input type="number" className="block w-full rounded-lg border border-slate-300 p-2.5" defaultValue="500" data-id="element-1074" />
                  </div>
                </div>
                <div data-id="element-1075">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1076">
                    International Flight Cap
                  </label>
                  <div className="flex items-center gap-2" data-id="element-1077">
                    <span className="text-slate-500" data-id="element-1078">$</span>
                    <input type="number" className="block w-full rounded-lg border border-slate-300 p-2.5" defaultValue="1500" data-id="element-1079" />
                  </div>
                </div>
                <div data-id="element-1080">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1081">
                    Hotel Nightly Cap
                  </label>
                  <div className="flex items-center gap-2" data-id="element-1082">
                    <span className="text-slate-500" data-id="element-1083">$</span>
                    <input type="number" className="block w-full rounded-lg border border-slate-300 p-2.5" defaultValue="250" data-id="element-1084" />
                  </div>
                </div>
                <div data-id="element-1085">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1086">
                    Advance Booking (Days)
                  </label>
                  <input type="number" className="block w-full rounded-lg border border-slate-300 p-2.5" defaultValue="14" data-id="element-1087" />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-1088">
                <div data-id="element-1089">
                  <h4 className="font-medium text-slate-900" data-id="element-1090">
                    Require Approval for All Trips
                  </h4>
                  <p className="text-xs text-slate-500" data-id="element-1091">
                    If disabled, only trips exceeding budget will require
                    approval.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-1092">
                  <input type="checkbox" className="sr-only peer" defaultChecked data-id="element-1093" />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" data-id="element-1094"></div>
                </label>
              </div>
            </div>}

          {step === 4 && <div className="space-y-6 animate-in fade-in slide-in-from-right-4" data-id="element-1095">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2" data-id="element-1096">
                <Users className="h-5 w-5 text-blue-600" data-id="element-1097" /> Invite Team
              </h2>
              <p className="text-sm text-slate-500" data-id="element-1098">
                Send invitations to your colleagues.
              </p>

              <div className="flex gap-2 items-end" data-id="element-1099">
                <div className="flex-1" data-id="element-1100">
                  <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-1101">
                    Email Address
                  </label>
                  <input type="email" value={newInvite.email} onChange={e => setNewInvite({
                ...newInvite,
                email: e.target.value
              })} placeholder="colleague@company.com" className="w-full rounded-lg border border-slate-300 p-2.5 focus:border-blue-500 focus:ring-blue-500" data-id="element-1102" />
                </div>
                <div className="w-40" data-id="element-1103">
                  <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-1104">
                    Role
                  </label>
                  <select value={newInvite.role} onChange={e => setNewInvite({
                ...newInvite,
                role: e.target.value
              })} className="w-full rounded-lg border border-slate-300 p-2.5 focus:border-blue-500 focus:ring-blue-500" data-id="element-1105">
                    <option data-id="element-1106">Traveler</option>
                    <option data-id="element-1107">Manager</option>
                    <option data-id="element-1108">Finance</option>
                    <option data-id="element-1109">Admin</option>
                  </select>
                </div>
                <button onClick={handleAddInvite} className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 flex items-center" data-id="element-1110">
                  <Plus className="h-4 w-4" data-id="element-1111" />
                </button>
              </div>

              <div className="border rounded-lg overflow-hidden" data-id="element-1112">
                <table className="min-w-full divide-y divide-slate-200" data-id="element-1113">
                  <thead className="bg-slate-50" data-id="element-1114">
                    <tr data-id="element-1115">
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1116">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1117">
                        Role
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1118">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200" data-id="element-1119">
                    {invites.map((invite, idx) => <tr key={idx} data-id="element-1120">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900" data-id="element-1121">
                          {invite.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500" data-id="element-1122">
                          {invite.role}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1123">
                          <button onClick={() => setInvites(invites.filter((_, i) => i !== idx))} className="text-red-600 hover:text-red-900" data-id="element-1124">
                            Remove
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>

        <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center" data-id="element-1125">
          <button onClick={() => step > 1 && setStep(step - 1)} disabled={step === 1} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium ${step === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-200'}`} data-id="element-1126">
            <ArrowLeft className="mr-2 h-4 w-4" data-id="element-1127" /> Back
          </button>

          <button onClick={() => step < 4 ? setStep(step + 1) : handleComplete()} className="flex items-center px-6 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 shadow-sm" data-id="element-1128">
            {step === 4 ? 'Complete Setup' : 'Next Step'}
            {step < 4 && <ArrowRight className="ml-2 h-4 w-4" data-id="element-1129" />}
          </button>
        </div>
      </div>
    </div>;
}